/**
 * Tone instruction builders — pure strings, no React, no network.
 *
 * WHY THE BRAND TONE TRAVELS INSIDE THE INSTRUCTION: `/api/content/rewrite`
 * has no brand grounding at all. Its system prompt carries no kit and no
 * voice, and the handler never loads one — unlike its sibling
 * `/api/content/caption`, which composes brand context via
 * `resolveActiveBrandKit`. Until that endpoint learns to load a voice
 * server-side, the only honest way to keep a rewrite in the user's voice is to
 * put the voice in the `instruction` field, which the endpoint already
 * forwards verbatim into the prompt. That is a workaround, not the fix; the
 * fix belongs in `server/api/content/rewrite.post.ts`.
 */

import { tonePresetById } from './tone-presets';

const KEEP_INTACT = 'Keep the same meaning, facts, and any hashtags or links.';

/** The full rewrite instruction for "put this caption in the brand's voice". */
export function brandToneInstruction(tone: string): string {
  return `Rewrite this caption in the brand's own voice: "${tone.trim()}". ${KEEP_INTACT}`;
}

/** The full rewrite instruction for one of the three presets. */
export function presetToneInstruction(presetId: string): string | null {
  const preset = tonePresetById(presetId);
  if (!preset) return null;
  return `Rewrite this caption so the voice is ${preset.voiceClause}. ${KEEP_INTACT}`;
}

/**
 * The full rewrite instruction for whichever tone the user just chose.
 * `null`/unknown ids and an empty brand tone return null so a caller can never
 * fire a rewrite whose instruction says nothing.
 */
export function toneInstructionFor(args: {
  toneId: string | null;
  brandTone: string | null;
}): string | null {
  const { toneId, brandTone } = args;
  const preset = presetToneInstruction(toneId ?? '');
  if (preset) return preset;
  const trimmed = (brandTone ?? '').trim();
  return trimmed ? brandToneInstruction(trimmed) : null;
}

/**
 * The clause appended to EVERY OTHER caption edit — the verbs, the platform
 * rewrite, the fan-out. Without it, choosing a tone and then clicking
 * "Shorten" silently washes the chosen voice back out, which is what made the
 * old fire-and-forget tone menu feel like it never stuck.
 *
 * Falls back to the brand tone when the post carries no override, so the
 * per-post choice layers ON the brand voice rather than replacing it. Returns
 * null when there is nothing honest to say.
 */
export function resolveToneInstruction(args: {
  toneOverride: string | null;
  brandTone: string | null;
}): string | null {
  const { toneOverride, brandTone } = args;
  const preset = tonePresetById(toneOverride);
  if (preset) return `Keep the voice ${preset.voiceClause}.`;
  const trimmed = (brandTone ?? '').trim();
  if (!trimmed) return null;
  return `Keep the voice consistent with the brand's own tone: "${trimmed}".`;
}

/**
 * The tone control's trigger label. Returns null when the post carries no
 * override, so the caller keeps its own default ("Tone" / "Adjust tone") — a
 * post inheriting the brand voice has not had a tone CHOSEN on it, and
 * labelling it as if it had would be a small lie the user cannot undo.
 */
export function toneLabelFor(toneOverride: string | null): string | null {
  return tonePresetById(toneOverride)?.label ?? null;
}
